/**
 * RS Customizer — Kids Necklace
 * Visual preview renderer for kids necklace customization.
 * Fetches metaobject config (SVG path guide, backgrounds, letter images via GraphQL),
 * injects a gallery preview slide, and renders per-character images positioned
 * along the SVG path as the user types.
 *
 * Rendering approach: SVG path-based — characters are <img> elements placed at
 * points along the path using getPointAtLength(), rotated to follow the curve.
 *
 * Always uppercase. No icon support. No end-cap beads.
 *
 * MTO input validation is handled by rs-product-mto-input.js. ATC gating is managed by rs-product-core.js.
 * This file handles ONLY the visual preview.
 */
(function () {
  'use strict';

  const METAOBJECT_TYPE = 'customizer_kids_necklace_set';
  const LIQUID_VIEW = 'metaobject-kids-necklace';

  const GRAPHQL_QUERY = `
    query getKidsNecklaceMetaobject($handle: MetaobjectHandleInput!) {
      metaobject(handle: $handle) {
        handle
        type
        fields {
          key
          value
          references(first: 250) {
            edges {
              node {
                ... on Metaobject {
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image { url width height }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  // Scaling constants matching the old implementation.
  const BASELINE_WIDTH = 1110;
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1110;

  let _injectedSlideId = null;
  let _baseConfig = null;
  let _letters = null;
  let _backgroundsData = null;
  let _currentVariantTitle = null;
  let _resizeTimer = null;

  // ─── DOM helpers ────────────────────────────────────────────────

  function _getMtoText() {
    const fields = document.querySelectorAll('[data-mto-field]');
    let text = '';
    fields.forEach(function (field) {
      text += field.value;
    });
    return text.trim().toUpperCase();
  }

  // ─── Data fetching ──────────────────────────────────────────────

  async function _fetchBaseConfig() {
    const response = await fetch(
      window.RSProduct.config.productUrl + '?view=' + LIQUID_VIEW
    );
    return response.json();
  }

  async function _fetchLetters() {
    if (!window.RSGraphQL) return null;

    try {
      const data = await window.RSGraphQL.executeQuery(GRAPHQL_QUERY, {
        handle: {
          handle: window.RSProduct.config.productHandle,
          type: METAOBJECT_TYPE,
        },
      });
      return _extractLetters(data);
    } catch (err) {
      console.error('[KidsNecklace] GraphQL fetch failed:', err);
      return null;
    }
  }

  function _extractLetters(data) {
    if (!data || !data.metaobject) return {};

    const letters = {};
    const lettersField = data.metaobject.fields.find(function (f) {
      return f.key === 'letters';
    });

    if (!lettersField || !lettersField.references || !lettersField.references.edges) {
      return {};
    }

    lettersField.references.edges.forEach(function (edge) {
      const node = edge.node;
      const entry = {};

      node.fields.forEach(function (field) {
        switch (field.key) {
          case 'character':
            entry.character = field.value;
            break;
          case 'name':
            entry.name = field.value;
            break;
          case 'image':
            if (field.reference && field.reference.image) {
              entry.image = field.reference.image.url;
              entry.image_width = field.reference.image.width || 60;
              entry.image_height = field.reference.image.height || 60;
            }
            break;
          case 'rotation':
            entry.rotation = parseFloat(field.value) || 0;
            break;
          case 'letter_spacing':
            entry.letter_spacing = parseInt(field.value, 10) || 0;
            break;
          case 'letter_spacing_override':
            entry.letter_spacing_override = parseInt(field.value, 10) || 0;
            break;
          default:
            break;
        }
      });

      if (entry.character && entry.image) {
        letters[entry.character] = {
          name: entry.name || '',
          image: entry.image,
          image_width: entry.image_width || 60,
          image_height: entry.image_height || 60,
          rotation: entry.rotation || 0,
          letter_spacing: entry.letter_spacing || 0,
          letter_spacing_override: entry.letter_spacing_override || 0,
        };
      }
    });

    return letters;
  }

  // ─── Background management ──────────────────────────────────────

  function _getBackgroundForVariant(variantTitle) {
    if (!_backgroundsData || !variantTitle) return null;

    const key = variantTitle.toLowerCase();
    let match = null;

    Object.entries(_backgroundsData).forEach(function (pair) {
      if (pair[0].toLowerCase() === key || key.includes(pair[0].toLowerCase())) {
        match = pair[1];
      }
    });

    // Fallback to first available background
    if (!match) {
      const keys = Object.keys(_backgroundsData);
      if (keys.length > 0) match = _backgroundsData[keys[0]];
    }

    return match;
  }

  // ─── Letter key lookup ──────────────────────────────────────────

  function _getLetterKey(char) {
    if (_letters && _letters[char]) return char;

    if (_letters) {
      const keys = Object.keys(_letters);
      const found = keys.find(function (k) {
        return k.toLowerCase() === char.toLowerCase();
      });
      if (found) return found;
    }

    return char.toUpperCase();
  }

  // ─── Canvas management ──────────────────────────────────────────

  function _getCanvas() {
    if (!_injectedSlideId) return null;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    if (!slide) return null;

    const customizerView = slide.querySelector('.customizer-view');
    if (!customizerView) return null;

    let canvas = customizerView.querySelector('.rs-customizer-canvas');
    if (!canvas) {
      canvas = _buildCanvas(customizerView);
    }
    return canvas;
  }

  function _buildCanvas(customizerView) {
    const canvas = document.createElement('div');
    canvas.className = 'rs-customizer-canvas';

    // Inject SVG path guide — kept in DOM (hidden) for getPointAtLength() measurements.
    if (_baseConfig && _baseConfig.svg_guide_content) {
      const temp = document.createElement('div');
      temp.innerHTML = _baseConfig.svg_guide_content;

      let svgEl = temp.querySelector('svg');
      if (!svgEl) {
        // svg_guide_content might be just a <path> element — wrap it in an SVG.
        const pathEl = temp.querySelector('path');
        if (pathEl) {
          svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          const viewBoxWidth = (_baseConfig.svg_view_box_width) || BASELINE_WIDTH;
          const viewBoxHeight = (_baseConfig.svg_view_box_height) || 880;
          svgEl.setAttribute('viewBox', '0 0 ' + viewBoxWidth + ' ' + viewBoxHeight);
          svgEl.appendChild(pathEl.cloneNode(true));
        }
      }

      if (svgEl) {
        svgEl.classList.add('rs-customizer-path-svg');
        svgEl.setAttribute('aria-hidden', 'true');
        svgEl.setAttribute('focusable', 'false');
        const pathEl = svgEl.querySelector('path');
        if (pathEl) {
          pathEl.classList.add('rs-customizer-path');
        }
        canvas.appendChild(svgEl);
      }
    }

    const letterContainer = document.createElement('div');
    letterContainer.className = 'rs-customizer-letters';
    canvas.appendChild(letterContainer);

    customizerView.appendChild(canvas);
    return canvas;
  }

  function _getLetterContainer(canvas) {
    return canvas ? canvas.querySelector('.rs-customizer-letters') : null;
  }

  function _getPathElement(canvas) {
    if (!canvas) return null;
    const svgEl = canvas.querySelector('.rs-customizer-path-svg');
    if (!svgEl) return null;
    return svgEl.querySelector('path');
  }

  // ─── Letter rendering ───────────────────────────────────────────

  function _renderLetters() {
    const text = _getMtoText();
    const canvas = _getCanvas();
    if (!canvas) return;

    const letterContainer = _getLetterContainer(canvas);
    if (!letterContainer) return;

    // Only remove necklace letter items — not the SVG path element or container structure.
    letterContainer.querySelectorAll('[data-rs-customizer-item]').forEach(function (el) {
      el.remove();
    });

    if (!text || !_letters) return;

    const chars = text.split('').filter(function (ch) {
      const letterKey = _getLetterKey(ch);
      return _letters[letterKey] && _letters[letterKey].image;
    });

    if (!chars.length) return;

    const pathEl = _getPathElement(canvas);
    if (!pathEl) return;

    const positions = _calculatePositions(chars, pathEl, letterContainer);
    _mountLetters(chars, positions, letterContainer);
  }

  function _calculatePositions(chars, pathEl, letterContainer) {
    const pathLength = pathEl.getTotalLength();
    if (!Number.isFinite(pathLength) || pathLength <= 0) return [];

    const containerRect = letterContainer.getBoundingClientRect();
    const viewBoxWidth = (_baseConfig && _baseConfig.svg_view_box_width) || BASELINE_WIDTH;
    const viewBoxHeight = (_baseConfig && _baseConfig.svg_view_box_height) || 880;
    const scaleX = containerRect.width / viewBoxWidth;
    const scaleY = containerRect.height / viewBoxHeight;

    // Calculate total spacing needed to center the sequence along the path.
    let totalSpacing = 0;
    for (let i = 0; i < chars.length - 1; i++) {
      totalSpacing += _getCharSpacing(chars[i], chars[i + 1], containerRect.width, scaleX);
    }

    let startPos = (pathLength - totalSpacing) / 2;
    if (startPos < 0) startPos = 0;

    const positions = [];
    let currentPos = startPos;
    const yOffset = (_baseConfig && _baseConfig.y_offset) || 0;

    for (let i = 0; i < chars.length; i++) {
      const clamped = Math.min(Math.max(currentPos, 0), pathLength);
      const point = pathEl.getPointAtLength(clamped);
      const angle = _getAngleAt(clamped, pathEl, pathLength);
      const letterKey = _getLetterKey(chars[i]);
      const letterData = _letters[letterKey];
      const rotation = letterData ? (letterData.rotation || 0) : 0;

      positions.push({
        x: point.x * scaleX,
        y: (point.y + yOffset) * scaleY,
        rotation: angle + rotation,
        data: letterData,
        char: chars[i],
      });

      if (i < chars.length - 1) {
        const spacing = _getCharSpacing(chars[i], chars[i + 1], containerRect.width, scaleX);
        if (Number.isFinite(spacing) && spacing > 0) {
          currentPos += spacing;
        }
      }
    }

    return positions;
  }

  function _mountLetters(chars, positions, letterContainer) {
    positions.forEach(function (posData, i) {
      if (!posData.data || !posData.data.image) return;

      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-rs-customizer-item', String(i));
      wrapper.className = 'rs-customizer-item';

      const img = document.createElement('img');
      img.src = posData.data.image;
      img.alt = posData.char;
      img.className = 'rs-customizer-letter-img';

      const height = _getResponsiveHeight();
      const width = _getLetterWidth(posData.char, height);
      img.style.width = width + 'px';
      img.style.height = height + 'px';

      img.style.opacity = '0';
      img.style.transition = 'opacity 0.3s';
      img.addEventListener('load', function () { img.style.opacity = '1'; });
      img.addEventListener('error', function () { img.style.opacity = '1'; });

      wrapper.style.left = posData.x + 'px';
      wrapper.style.top = posData.y + 'px';
      wrapper.style.transform =
        'translate(-50%, -50%) rotate(' + posData.rotation + 'deg)';

      wrapper.appendChild(img);
      letterContainer.appendChild(wrapper);
    });
  }

  // ─── Sizing helpers ─────────────────────────────────────────────

  function _getScaleFactor() {
    if (!_injectedSlideId) return 1.0;

    const slide = document.querySelector(
      '[data-rs-injected="' + _injectedSlideId + '"]'
    );
    const containerWidth = slide ? slide.getBoundingClientRect().width : BASELINE_WIDTH;

    let scale;
    if (containerWidth < MIN_WIDTH) {
      scale = 0.6;
    } else if (containerWidth > MAX_WIDTH) {
      scale = 1.0;
    } else {
      const progress = (containerWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
      scale = 0.6 + progress * 0.4;
    }

    if (window.innerWidth <= 1024) scale *= 0.8;

    return scale;
  }

  function _getResponsiveHeight() {
    const baseHeight = (_baseConfig && _baseConfig.letter_height) || 60;
    const letterScale = (_baseConfig && _baseConfig.letter_scale) || 1.0;
    const scaledHeight = baseHeight * letterScale * _getScaleFactor();
    return Number.isFinite(scaledHeight) && scaledHeight > 0 ? scaledHeight : 60;
  }

  function _getLetterWidth(char, height) {
    const letterKey = _getLetterKey(char);
    const letterData = _letters && _letters[letterKey];

    if (letterData && letterData.image_width && letterData.image_height) {
      return height * (letterData.image_width / letterData.image_height);
    }

    const baseWidth = (_baseConfig && _baseConfig.letter_width) || 60;
    const letterScale = (_baseConfig && _baseConfig.letter_scale) || 1.0;
    const scaledWidth = baseWidth * letterScale * _getScaleFactor();
    return Number.isFinite(scaledWidth) && scaledWidth > 0 ? scaledWidth : 60;
  }

  /**
   * Returns the arc length (in viewBox units) to advance along the path
   * when moving from currentChar to nextChar.
   *
   * @param {string} currentChar
   * @param {string} nextChar
   * @param {number} containerWidth - rendered container width in px
   * @param {number} scaleX - px per viewBox unit
   */
  function _getCharSpacing(currentChar, nextChar, containerWidth, scaleX) {
    const height = _getResponsiveHeight();
    const currentWidth = _getLetterWidth(currentChar, height);
    const nextWidth = _getLetterWidth(nextChar, height);

    // Centre-to-centre distance = half of each letter's width.
    const halfWidths = currentWidth / 2 + nextWidth / 2;

    // Per-letter or global spacing gap (in display pixels, pre-scale).
    const letterKey = _getLetterKey(currentChar);
    const letterData = _letters && _letters[letterKey];

    let spacingGap;
    if (letterData && typeof letterData.letter_spacing_override === 'number' && letterData.letter_spacing_override !== 0) {
      spacingGap = letterData.letter_spacing_override;
    } else if (letterData && typeof letterData.letter_spacing === 'number' && letterData.letter_spacing !== 0) {
      spacingGap = letterData.letter_spacing;
    } else {
      spacingGap = (_baseConfig && _baseConfig.global_spacing !== undefined)
        ? _baseConfig.global_spacing
        : 0;
    }

    // Scale the gap proportionally to the container.
    const containerScale = Math.min((containerWidth || BASELINE_WIDTH) / BASELINE_WIDTH, 1.0);
    spacingGap *= containerScale;

    const finalSpacingPx = halfWidths + spacingGap;

    // Convert from display pixels back to viewBox units for path advancement.
    const result = scaleX > 0 ? finalSpacingPx / scaleX : finalSpacingPx;
    return Number.isFinite(result) && result > 0 ? result : 20;
  }

  function _getAngleAt(position, pathEl, pathLength) {
    const delta = Math.max(2, pathLength * 0.002);
    const p1 = pathEl.getPointAtLength(Math.max(0, position - delta));
    const p2 = pathEl.getPointAtLength(Math.min(pathLength, position + delta));
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
    return Number.isFinite(angle) ? angle : 0;
  }

  // ─── Event handlers ─────────────────────────────────────────────

  function _handleInput(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    _renderLetters();
  }

  function _handleInputFocus(event) {
    if (!event.target.matches('[data-mto-field]')) return;
    if (_injectedSlideId) {
      window.RSProduct.gallery.navigate(_injectedSlideId);
    }
  }

  function _handleVariantChange(event) {
    if (!event.data || event.data.sectionId !== window.RSProduct.sectionId) return;

    const variant = event.data.variant;
    if (!variant || !variant.title) return;

    _currentVariantTitle = variant.title;

    // Gallery HTML is replaced by rs-product-core's variant handler — remove old slide first.
    if (_injectedSlideId) {
      window.RSProduct.gallery.remove(_injectedSlideId);
      _injectedSlideId = null;
    }

    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    // Preserve user input across variant change.
    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
      if (_injectedSlideId) {
        window.RSProduct.gallery.navigate(_injectedSlideId);
      }
    }
  }

  function _onResize() {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      const text = _getMtoText();
      if (text) _renderLetters();
    }, 250);
  }

  // ─── Initialization ─────────────────────────────────────────────

  async function _init() {
    if (!window.RSProduct.config.productHandle) return;

    let baseConfig, letters;
    try {
      [baseConfig, letters] = await Promise.all([
        _fetchBaseConfig(),
        _fetchLetters(),
      ]);
    } catch (err) {
      console.error('[KidsNecklace] Data fetch failed:', err);
      return;
    }

    _baseConfig = baseConfig;
    _backgroundsData = baseConfig.backgrounds || {};
    _letters = letters || {};

    // Determine initial variant title from the config passed by rs-product-core.liquid.
    const initialVariantId = String(window.RSProduct.config.initialVariantId || '');
    const variantNames = baseConfig.variant_names || {};
    const fullName = variantNames[initialVariantId] || '';
    const parts = fullName.split(' - ');
    _currentVariantTitle = parts.length > 1 ? parts[parts.length - 1] : fullName;

    // Inject preview slide at index 1 (2nd gallery position).
    const backgroundUrl = _getBackgroundForVariant(_currentVariantTitle);
    if (backgroundUrl) {
      _injectedSlideId = window.RSProduct.gallery.inject(backgroundUrl, 1);
    }

    document.addEventListener('input', _handleInput);
    document.addEventListener('focusin', _handleInputFocus);
    window.addEventListener('resize', _onResize);

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, _handleVariantChange);
    }

    // Render letters if MTO field already has a value (e.g. back navigation).
    const existingText = _getMtoText();
    if (existingText) {
      _renderLetters();
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!window.RSProduct) return;
    _init();
  });
})();
